import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../hooks/use-toast';

import { useMutation } from '@apollo/client';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { LOGIN } from '../lib/graphql/mutations/customer';
import {
  LOGIN_WITH_PHONE,
  REQUEST_PASSWORD_RESET,
  RESET_PASSWORD,
  RESET_PASSWORD_WITH_PHONE,
} from '../lib/graphql/mutations/customer';
import usePhoneVerification from '../hooks/use-phone-verification';
import { useIsMobile } from '../hooks/use-mobile';
import { Loader2, MessageSquareText, ShieldAlert } from 'lucide-react';

const Auth = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [login, { loading }] = useMutation(LOGIN);

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email || !password) {
      toast({
        title: 'Алдаа',
        description: 'Please fill in all fields',
        variant: 'destructive',
      });
      return;
    }

    login({
      variables: {
        input: {
          login: email,
          password: password,
        },
      },
    })
      .then(({ data }) => {
        const { token } = data.customerLogin;
        localStorage.setItem('token', token);

        // Dispatch custom event to trigger immediate user data refetch
        window.dispatchEvent(new Event('authTokenChanged'));

        toast({
          title: 'Амжилттай',
          description: 'Амжилттай нэвтэрлээ',
        });
        navigate('/');
      })
      .catch((error) => {
        toast({
          title: 'Алдаа',
          description: error.message,
          variant: 'destructive',
        });
      });
  };

  // Prevent scrolling when component mounts
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div className='container mx-auto px-4 py-8 md:py-16 md:h-screen md:overflow-hidden'>
      <div className='max-w-md mx-auto'>
        <h1 className='text-3xl font-light text-center mb-8'>Нэвтрэх</h1>

        <Tabs defaultValue='sms'>
          <TabsList className='grid w-full grid-cols-2 mb-4'>
            <TabsTrigger value='sms' className='gap-1.5'>
              <MessageSquareText className='h-4 w-4' />
              SMS кодоор
            </TabsTrigger>
            <TabsTrigger value='password'>Нууц үгээр</TabsTrigger>
          </TabsList>

          <TabsContent value='password'>
            <Card className='p-6'>
              <form onSubmit={handleSignIn} className='space-y-4 mt-2'>
                <div>
                  <Label htmlFor='signin-email'>И-мэйл</Label>
                  <Input
                    id='signin-email'
                    name='email'
                    type='email'
                    required
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor='signin-password'>Нууц үг</Label>
                  <Input
                    id='signin-password'
                    name='password'
                    type='password'
                    required
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>

                <Button type='submit' className='w-full' disabled={loading}>
                  {loading ? 'Уншиж байна...' : 'Нэвтрэх'}
                </Button>
                <div className='text-center mt-4'>
                  <ForgotPasswordDialog />
                </div>
              </form>
            </Card>
          </TabsContent>

          <TabsContent value='sms'>
            <Card className='p-6'>
              <SmsLoginForm onAuthenticated={() => navigate('/')} />
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

/** One-tap SMS login via verify.mn (MO SMS to shortcode 144773). */
export const SmsLoginForm = ({ onAuthenticated }: { onAuthenticated: () => void }) => {
  const [phone, setPhone] = useState('');
  const isMobile = useIsMobile();
  const [loginWithPhone, { loading: loginLoading }] = useMutation(LOGIN_WITH_PHONE);
  const { session, starting, error, start, reset, checkStatus } = usePhoneVerification();

  useEffect(() => {
    if (session?.status !== 'VERIFIED') return;

    loginWithPhone({ variables: { sessionId: session.sessionId } })
      .then(({ data }) => {
        const token = data?.customerLoginWithPhone?.token;
        if (!token) {
          throw new Error('Нэвтрэх токен авч чадсангүй');
        }
        localStorage.setItem('token', token);
        window.dispatchEvent(new Event('authTokenChanged'));
        toast({
          title: 'Амжилттай',
          description: 'Амжилттай нэвтэрлээ',
        });
        onAuthenticated();
      })
      .catch((err) => {
        toast({
          title: 'Алдаа',
          description: err.message,
          variant: 'destructive',
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.status]);

  const handleStart = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!phone.trim()) return;
    await start(phone.trim(), 'LOGIN');
  };

  // Once verified, hide the send action immediately (each SMS costs the user )
  if (session) {
    const verified = session.status === 'VERIFIED';
    return (
      <div className='space-y-4 mt-2'>
        {verified ? (
          <div className='flex flex-col items-center gap-3 py-8 text-center'>
            <Loader2 className='h-6 w-6 animate-spin text-primary' />
            <p className='text-sm text-muted-foreground'>Утас баталгаажлаа. Нэвтэрч байна...</p>
          </div>
        ) : session.status === 'EXPIRED' ? (
          <div className='space-y-4 py-4 text-center'>
            <ShieldAlert className='h-10 w-10 mx-auto text-muted-foreground' />
            <p className='text-sm text-muted-foreground'>Кодын хугацаа дууслаа. Дахин оролдоно уу.</p>
            <Button
              className='w-full'
              variant='outline'
              onClick={() => {
                reset();
                setPhone('');
              }}
            >
              Дахин илгээх
            </Button>
          </div>
        ) : (
          <>
            <div className='rounded-lg border bg-muted/40 p-4 space-y-3'>
              <p className='text-sm leading-relaxed'>{session.displayInstruction}</p>
              {isMobile && (
                <a href={session.smsUri ?? undefined} className='block'>
                  <Button className='w-full h-12 text-base'>
                    <MessageSquareText className='mr-2 h-5 w-5' />
                    SMS илгээх
                  </Button>
                </a>
              )}
              {isMobile && (
                <p className='text-xs text-muted-foreground'>
                  Товчлуур дараад SMS апп нээгдэж, зөвхөн «Илгээх» товч дарахад хангалттай. SMS-ийг өөрийн дугаараас
                  илгээнэ үү.
                </p>
              )}
            </div>
            <div className='flex items-center justify-center gap-2 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Баталгаажуулалт хүлээж байна...
            </div>
            <Button variant='secondary' className='w-full' onClick={() => void checkStatus()}>
              Баталгаажуулалт шалгах
            </Button>
            <Button
              variant='ghost'
              className='w-full'
              onClick={() => {
                reset();
                setPhone('');
              }}
            >
              Цуцлах
            </Button>
          </>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleStart} className='space-y-4 mt-2'>
      <div>
        <Label htmlFor='sms-phone'>Утасны дугаар</Label>
        <Input
          id='sms-phone'
          type='tel'
          inputMode='numeric'
          placeholder='99119911'
          maxLength={12}
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
      </div>

      {(error || undefined) && <p className='text-sm text-destructive'>{error}</p>}

      <Button type='submit' className='w-full' disabled={starting || loginLoading}>
        {starting ? 'Уншиж байна...' : 'Нэвтрэх код авах'}
      </Button>
      <p className='text-xs text-muted-foreground text-center'>
        144773 дугаарт 6 оронтой кодтой SMS илгээж нэвтэрнэ үү.
      </p>
    </form>
  );
};

/** Forgot-password dialog: reset via email code (existing) or via verify.mn phone. */
export const ForgotPasswordDialog = () => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type='button' variant='link' className='text-sm text-gray-600 hover:text-gray-900'>
          Нууц үг сэргээх?
        </Button>
      </DialogTrigger>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle>Нууц үг сэргээх</DialogTitle>
          <DialogDescription>И-мэйл эсвэл утасны дугаараар сэргээнэ үү.</DialogDescription>
        </DialogHeader>
        {open && (
          <Tabs defaultValue='phone'>
            <TabsList className='grid w-full grid-cols-2 mb-2'>
              <TabsTrigger value='phone'>Утсаар</TabsTrigger>
              <TabsTrigger value='email'>И-мэйлээр</TabsTrigger>
            </TabsList>
            <TabsContent value='phone'>
              <PhoneResetForm onDone={() => setOpen(false)} />
            </TabsContent>
            <TabsContent value='email'>
              <EmailResetForm onDone={() => setOpen(false)} />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PhoneResetForm = ({ onDone }: { onDone: () => void }) => {
  const [phone, setPhone] = useState('');
  const isMobile = useIsMobile();
  const [newPassword, setNewPassword] = useState('');
  const [resetPassword, { loading: resetLoading }] = useMutation(RESET_PASSWORD_WITH_PHONE);
  const { session, starting, error, start, reset, checkStatus } = usePhoneVerification();

  const handleStart = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!phone.trim()) return;
    await start(phone.trim(), 'PASSWORD_RESET');
  };

  const handleReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!session) return;

    try {
      const { data } = await resetPassword({
        variables: {
          sessionId: session.sessionId,
          newPassword,
        },
      });

      if (data?.customerResetPasswordWithPhone) {
        toast({
          title: 'Амжилттай',
          description: 'Нууц үг шинэчлэгдлээ.',
        });
        onDone();
      } else {
        throw new Error('Нууц үг шинэчлэхэд алдаа гарлаа');
      }
    } catch (err: any) {
      toast({
        title: 'Алдаа',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  if (session) {
    if (session.status === 'VERIFIED') {
      return (
        <form onSubmit={handleReset} className='space-y-4'>
          <div>
            <Label htmlFor='reset-new-password'>Шинэ нууц үг</Label>
            <Input
              id='reset-new-password'
              type='password'
              required
              minLength={6}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <Button type='submit' className='w-full' disabled={resetLoading}>
            {resetLoading ? 'Уншиж байна...' : 'Нууц үг солих'}
          </Button>
        </form>
      );
    }

    if (session.status === 'EXPIRED') {
      return (
        <div className='space-y-4 py-4 text-center'>
          <ShieldAlert className='h-10 w-10 mx-auto text-muted-foreground' />
          <p className='text-sm text-muted-foreground'>Кодын хугацаа дууслаа. Дахин оролдоно уу.</p>
          <Button
            className='w-full'
            variant='outline'
            onClick={() => {
              reset();
              setPhone('');
            }}
          >
            Дахин илгээх
          </Button>
        </div>
      );
    }

    return (
      <div className='space-y-4'>
        <div className='rounded-lg border bg-muted/40 p-4 space-y-3'>
          <p className='text-sm leading-relaxed'>{session.displayInstruction}</p>
          {isMobile && (
            <a href={session.smsUri ?? undefined} className='block'>
              <Button className='w-full h-12'>
                <MessageSquareText className='mr-2 h-5 w-5' />
                SMS илгээх
              </Button>
            </a>
          )}
        </div>
        <div className='flex items-center justify-center gap-2 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' />
          Баталгаажуулалт хүлээж байна...
        </div>
        <Button variant='secondary' className='w-full' onClick={() => void checkStatus()}>
          Баталгаажуулалт шалгах
        </Button>
        <Button
          variant='ghost'
          className='w-full'
          onClick={() => {
            reset();
            setPhone('');
          }}
        >
          Цуцлах
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleStart} className='space-y-4'>
      <div>
        <Label htmlFor='reset-phone'>Утасны дугаар</Label>
        <Input
          id='reset-phone'
          type='tel'
          inputMode='numeric'
          placeholder='99119911'
          maxLength={12}
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
      </div>
      {(error || undefined) && <p className='text-sm text-destructive'>{error}</p>}
      <Button type='submit' className='w-full' disabled={starting}>
        {starting ? 'Уншиж байна...' : 'Баталгаажуулах код авах'}
      </Button>
    </form>
  );
};

const EmailResetForm = ({ onDone }: { onDone: () => void }) => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [step, setStep] = useState<'request' | 'confirm'>('request');

  const [requestReset, { loading: requestLoading }] = useMutation(REQUEST_PASSWORD_RESET);
  const [resetPassword, { loading: resetLoading }] = useMutation(RESET_PASSWORD);

  const handleRequest = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      await requestReset({ variables: { email: email.trim() } });
      toast({
        title: 'Амжилттай',
        description: 'Сэргээх код и-мэйл рүү тань илгээгдлээ.',
      });
      setStep('confirm');
    } catch (err: any) {
      toast({
        title: 'Алдаа',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  const handleConfirm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      await resetPassword({
        variables: {
          email: email.trim(),
          code: code.trim(),
          newPassword,
        },
      });
      toast({
        title: 'Амжилттай',
        description: 'Нууц үг шинэчлэгдлээ.',
      });
      onDone();
    } catch (err: any) {
      toast({
        title: 'Алдаа',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  if (step === 'request') {
    return (
      <form onSubmit={handleRequest} className='space-y-4'>
        <div>
          <Label htmlFor='reset-email'>И-мэйл</Label>
          <Input id='reset-email' type='email' required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <Button type='submit' className='w-full' disabled={requestLoading}>
          {requestLoading ? 'Уншиж байна...' : 'Код илгээх'}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleConfirm} className='space-y-4'>
      <div>
        <Label htmlFor='reset-code'>Сэргээх код</Label>
        <Input
          id='reset-code'
          inputMode='numeric'
          maxLength={6}
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor='reset-email-new-password'>Шинэ нууц үг</Label>
        <Input
          id='reset-email-new-password'
          type='password'
          required
          minLength={6}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
      </div>
      <Button type='submit' className='w-full' disabled={resetLoading}>
        {resetLoading ? 'Уншиж байна...' : 'Нууц үг солих'}
      </Button>
    </form>
  );
};

export default Auth;
